/**
 * Membership requests — the committee's side of the join pipeline.
 *
 *   Request → Approve & send payment link → member pays + sets password
 *   → Activate → member can sign in.
 *
 * Each card shows where in that pipeline the person stands, and offers only
 * the action that stage needs. Links are one-time: resending voids the old
 * URL, so a link can never admit two people.
 */

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ageFrom, bibNo, feeFor, formatDate, formatMoney, memberFee, timeAgo, TIERS } from '../../data';
import { useClubData } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { DeclineRequestSheet } from '../../components/sheets/ClubSheets';
import {
  PayLinkSheet,
  RecordJoinCashSheet,
  RecordPaymentSheet,
} from '../../components/sheets/MoneySheets';
import { MemberCreatedSheet } from '../../components/sheets/MemberCreatedSheet';
import { Button, Empty, Eyebrow, Pill, ViewTitle } from '../../components/ui';
import { useFees, useGrounds } from '../../context/ClubContext';

const STAGES = ['Request', 'Link sent', 'Paid', 'Active'];

/*
 * The committee-added journey. Four stages now, mirroring a stranger's
 * request minus the approval step (the person was vouched for by whoever
 * added them): Added → Link sent → Paid → ACTIVE. Until the committee clicks
 * Activate, the member is `status: 'pending'` — off the roster screens and
 * unable to sign in, exactly like a paid join request (owner's direction,
 * 17 Aug 2026).
 */
const ADDED_STAGES = ['Added', 'Link sent', 'Paid', 'Active'];

/**
 * How the joining fee arrived, for the paid panel. Only set when the committee
 * recorded it by hand — a request paid on the link carries no method, and
 * saying nothing is right: the link is the assumption.
 */
const PAID_BY = {
  cash: 'cash',
  upi: 'UPI',
  bank: 'bank transfer',
};

/** How far along a committee-added member is, as an index into ADDED_STAGES. */
function addedStageIndex(m, paid) {
  if (paid) return 2;
  return m.linkSent ? 1 : 0;
}

/** How far along the pipeline a request is, as an index into STAGES. */
function stageIndex(r) {
  if (r.status === 'approved') return 3;
  if (r.status === 'paid') return 2;
  if (r.status === 'link_sent') return 1;
  return 0;
}

export function RequestsPage() {
  const { db, sendJoinLink, activateRequest, activateMember } = useClubData();
  const grounds = useGrounds();
  const { openSheet, toast } = useUI();

  /*
   * NEWEST FIRST in every section (owner's direction, 2 Oct 2026): the person
   * the committee is thinking about is almost always the one who just applied
   * or was just added, and they used to be at the bottom of a long list.
   */
  const newest = (a, b) => b.ts - a.ts;
  const inbox = db.requests.filter((r) => r.status === 'pending').sort(newest);
  const paid = db.requests.filter((r) => r.status === 'paid').sort(newest);
  const waiting = db.requests.filter((r) => r.status === 'link_sent').sort(newest);
  const handled = db.requests
    .filter((r) => r.status === 'approved' || r.status === 'declined')
    .sort((a, b) => b.ts - a.ts);

  /*
   * Members the committee added who are still in the pipeline. `pending` IS
   * the pipeline now — they stay here from the moment the add-member form
   * saves until the committee activates them, whether the money came by link
   * or by cash. Life tiers never carry a duesPaidTill, so "paid" also checks
   * the payment book.
   */
  /*
   * `?highlight=<memberId>` — set by the add-member form (cash) and by the
   * "Open requests" button after an add. That person goes FIRST, gets a gold
   * frame, and is scrolled into view, so the committee records the cash or
   * sends the link without hunting for them.
   */
  const [params] = useSearchParams();
  const highlight = params.get('highlight');
  // Chose cash on the add-member form: make "Record cash" the main button.
  const preferCash = params.get('pay') === 'cash';
  const committeeAdded = db.members
    .filter((m) => m.status === 'pending')
    .sort(
      (a, b) =>
        (String(b.id) === highlight) - (String(a.id) === highlight) ||
        String(b.joined ?? '').localeCompare(String(a.joined ?? '')) ||
        Number(b.id) - Number(a.id),
    );
  const highlightedAdded = highlight
    ? committeeAdded.some((m) => String(m.id) === highlight)
    : false;
  const isPaid = (m) =>
    Boolean(m.duesPaidTill) || db.payments.some((p) => p.memberId === m.id);
  const paidPending = committeeAdded.filter(isPaid);

  const needsAction = inbox.length + paid.length + paidPending.length;

  async function approveAndSend(request) {
    try {
      const token = await sendJoinLink(request.id);
      toast(`Payment link created for ${request.name.split(' ')[0]}`);
      return token;
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not create the link');
      return null;
    }
  }

  async function activate(request) {
    try {
      const member = await activateRequest(request.id);
      toast(`${request.name.split(' ')[0]} is now active · bib ${bibNo(member.bib)}`);
      // Straight into the sign-in sheet: if they onboarded it confirms their
      // password is set; if not, it says the set-password email has gone and
      // offers to resend it. The committee never handles a password.
      openSheet(<MemberCreatedSheet memberId={member.memberId ?? member.id} />);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not activate that membership');
    }
  }

  /** The same final approval, for a member the committee added by hand. */
  async function activateAdded(m) {
    try {
      await activateMember(m.id);
      toast(`${m.name.split(' ')[0]} is now active · bib ${bibNo(m.bib)}`);
      openSheet(<MemberCreatedSheet memberId={m.id} />);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not activate that membership');
    }
  }

  const addedSection = (placement) => (
    committeeAdded.length > 0 && placement && (
        <>
          <Eyebrow right={`${committeeAdded.length} in the pipeline`}>
            Added by the committee
          </Eyebrow>
          {committeeAdded.map((m) => (
            <AddedMemberCard
              key={m.id}
              member={m}
              paid={isPaid(m)}
              payment={db.payments.find((p) => p.memberId === m.id) ?? null}
              grounds={grounds}
              onPayLink={(x) => openSheet(<PayLinkSheet member={x} />)}
              onCash={(x) => openSheet(<RecordPaymentSheet member={x} />)}
              onActivate={(x) => void activateAdded(x)}
              highlighted={String(m.id) === highlight}
              preferCash={preferCash && String(m.id) === highlight}
            />
          ))}
        </>
      )
  );

  return (
    <>
      <ViewTitle
        title="Membership requests"
        sub="Approve → they pay on a one-time link and set a password → you activate"
      />

      {needsAction > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border border-gold-100 bg-gold-50 px-4 py-3.5">
          <div>
            <div className="font-market text-[17px] font-bold tracking-[-0.015em]">
              {needsAction} need{needsAction === 1 ? 's' : ''} your action
            </div>
            <div className="mt-0.5 font-market text-[10.5px] text-copy-muted">
              {paid.length + paidPending.length > 0 &&
                `${paid.length + paidPending.length} paid and waiting for activation`}
              {paid.length + paidPending.length > 0 && inbox.length > 0 && ' · '}
              {inbox.length > 0 && `${inbox.length} new request${inbox.length > 1 ? 's' : ''}`}
            </div>
          </div>
        </div>
      )}

      {/* The person just added comes before everything else. */}
      {addedSection(highlightedAdded)}

      {paid.length > 0 && (
        <>
          <Eyebrow>Paid — activate to let them in</Eyebrow>
          {paid.map((r) => (
            <RequestCard key={r.id} request={r} db={db} onActivate={() => void activate(r)} />
          ))}
        </>
      )}

      <Eyebrow>New requests</Eyebrow>
      {inbox.length ? (
        inbox.map((r) => (
          <RequestCard
            key={r.id}
            request={r}
            db={db}
            onSendLink={() => approveAndSend(r)}
            onDecline={() =>
              openSheet(<DeclineRequestSheet requestId={r.id} name={r.name} phone={r.phone} />)
            }
          />
        ))
      ) : (
        <Empty title="Nothing waiting">New requests will appear here.</Empty>
      )}

      {waiting.length > 0 && (
        <>
          <Eyebrow>Waiting on the member to pay</Eyebrow>
          {waiting.map((r) => (
            <RequestCard
              key={r.id}
              request={r}
              db={db}
              onResend={() => approveAndSend(r)}
              onCash={() => openSheet(<RecordJoinCashSheet request={r} />)}
              onDecline={() =>
                openSheet(<DeclineRequestSheet requestId={r.id} name={r.name} phone={r.phone} />)
              }
            />
          ))}
        </>
      )}

      {addedSection(!highlightedAdded)}

      {handled.length > 0 && (
        <>
          <Eyebrow>Handled</Eyebrow>
          {handled.map((r) => (
            <RequestCard key={r.id} request={r} db={db} />
          ))}
        </>
      )}
    </>
  );
}

/**
 * Each stage owns a colour, so the dot tells you WHERE a request is stuck
 * without reading the label — the whole pipeline in one amber run said only
 * "some progress happened".
 *
 *   Request   indigo   — in the queue, nothing asked of anyone yet
 *   Link sent amber    — waiting on the member; this is the one that stalls
 *   Paid      emerald  — money is in
 *   Active    ink      — done, they can sign in
 */
const STAGE_TONE = [
  { dot: 'bg-indigo-500', line: 'bg-indigo-300' },
  { dot: 'bg-gold-500', line: 'bg-gold-400' },
  { dot: 'bg-sea-600', line: 'bg-sea-300' },
  { dot: 'bg-ink-800', line: 'bg-ink-800' },
];

/**
 * The pipeline tracker.
 *
 * Takes its labels rather than closing over STAGES, because two different
 * journeys end up on this screen: a stranger applying (four stages, ending in
 * activation) and someone the committee added directly (three, because there
 * was never anything to approve). One tracker keeps them looking like the same
 * process, which for the committee they are — "who is joining, and where are
 * they stuck".
 */
function Stepper({ stages = STAGES, at, declined = false }) {
  return (
    <div className="mt-3 flex items-center gap-1.5">
      {stages.map((label, i) => {
        const reached = !declined && i <= at;
        const tone = STAGE_TONE[i];
        return (
          <div key={label} className="flex flex-1 items-center gap-1.5">
            <div className="flex flex-col items-center gap-1 sm:flex-row sm:gap-1.5">
              <span
                className={`grid h-4 w-4 flex-none place-items-center rounded-full text-[8px] font-bold text-white transition-all duration-300 ${
                  declined ? 'bg-line-strong' : reached ? tone.dot : 'bg-line'
                }`}
              >
                {i < at || (i === at && at === stages.length - 1) ? '✓' : ''}
              </span>
              <span
                className={`font-market text-[8px] uppercase tracking-wide sm:text-[9px] ${
                  reached ? 'text-copy' : 'text-copy-label'
                }`}
              >
                {label}
              </span>
            </div>
            {i < stages.length - 1 && (
              /* the connector carries the colour of the stage it leaves */
              <span
                className={`h-px flex-1 transition-all duration-300 ${
                  !declined && i < at ? tone.line : 'bg-line'
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

const CARD_TONE = {
  pending: 'border-l-gold-500',
  link_sent: 'border-l-gold-500',
  paid: 'border-l-sea-600',
  approved: 'border-l-sea-300 opacity-70',
  declined: 'border-l-line-strong opacity-60',
};

function RequestCard({ request: r, db, onSendLink, onResend, onCash, onActivate, onDecline }) {
  const grounds = useGrounds();
  const fees = useFees();
  const { toast } = useUI();
  const age = ageFrom(r.dob);
  const isMinor = age !== null && age < 18;

  // The live link for this request, if one exists.
  const activeLink = (db.payLinks ?? []).find(
    (l) => l.requestId === r.id && l.status === 'active',
  );
  const joinUrl = activeLink ? `${window.location.origin}/join/${activeLink.token}` : null;
  const [copied, setCopied] = useState(false);

  async function copy() {
    if (!joinUrl) return;
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast('Could not copy — long-press the link instead');
    }
  }

  return (
    <div
      className={`mb-3 border border-line border-l-[3px] bg-white p-4 ${CARD_TONE[r.status] ?? ''}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2.5">
        <div className="font-market text-[19px] font-bold tracking-[-0.025em]">{r.name}</div>
        <div className="flex items-baseline gap-2">
          {r.status === 'pending' && <Pill tone="pend">New request</Pill>}
          {r.status === 'link_sent' && <Pill tone="soon">Link sent · unpaid</Pill>}
          {r.status === 'paid' && <Pill tone="ok">Paid {formatMoney(r.amount)}</Pill>}
          {r.status === 'approved' && <Pill tone="ok">Active · bib {bibNo(r.bib ?? 0)}</Pill>}
          {r.status === 'declined' && <Pill tone="late">Declined</Pill>}
          <span className="font-market text-[9.5px] text-copy-label">{timeAgo(r.ts)}</span>
        </div>
      </div>

      <Stepper at={stageIndex(r)} declined={r.status === 'declined'} />

      <div className="mt-3 grid grid-cols-2 gap-px overflow-hidden border border-line bg-line min-[560px]:grid-cols-3">
        <Cell k="Mobile" v={r.phone} mono />
        <Cell k="Email" v={r.email ?? '—'} mono />
        <Cell k="Age" v={`${age ?? '—'}${isMinor ? ' · minor' : ''}`} mono />
        <Cell k="Tier" v={`${TIERS[r.tier].label} ₹${r.amount ?? feeFor(fees, r.tier, 'join')}`} />
        <Cell k="Home ground" v={grounds[r.ground]?.split(' ')[0] ?? '—'} />
        <Cell k="Blood group" v={r.blood ?? '—'} mono />
        <Cell
          k={isMinor ? 'Guardian' : 'Emergency'}
          v={(isMinor ? r.guardianName || r.emName : r.emName) ?? '—'}
          span="col-span-2 min-[560px]:col-span-3"
        />
      </div>

      {r.note && (
        <div className="mt-3 bg-ground px-3 py-2.5 text-[12.5px] leading-relaxed text-copy-muted">
          “{r.note}”
        </div>
      )}

      {/* Paid: the money is in — show the proof and the one remaining action. */}
      {r.status === 'paid' && (
        <div className="mt-3 border border-sea-300 bg-sea-50 px-3.5 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2 font-market text-[11px] text-sea-600">
            <span>
              Paid {formatMoney(r.amount)} on {formatDate(r.paidOn)}
              {r.method ? ` · ${PAID_BY[r.method] ?? r.method}` : ''} · receipt <b>{r.receipt}</b>
            </span>
            <span>
              {r.onboarded
                ? 'Details confirmed · password set'
                : 'Has not set a password — they can use “Forgot password” once activated'}
            </span>
          </div>
          {/* The member's own screenshot of the transfer. The committee has to
              be able to READ it — the amount and the UTR line — so it opens
              full size rather than only sitting here as a thumbnail. */}
          {r.proofUrl && (
            <a
              href={r.proofUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-2.5 flex items-center gap-3 border border-sea-300 bg-white p-2 transition-colors hover:border-sea-600"
            >
              <img
                src={r.proofUrl}
                alt="Payment screenshot from the member"
                className="h-[64px] w-[48px] flex-none border border-line object-cover"
              />
              <span className="min-w-0 flex-1">
                <span className="block font-market text-[11.5px] font-bold text-ink-700">
                  Payment screenshot attached
                </span>
                <span className="block font-market text-[10px] text-copy-muted">
                  Sent by {r.name.split(' ')[0]} — tap to open full size
                </span>
              </span>
            </a>
          )}
        </div>
      )}

      {/* Link sent: surface the URL so the committee can re-share it. */}
      {r.status === 'link_sent' && joinUrl && (
        <div className="mt-3 border border-dashed border-line-strong bg-ground p-3">
          <div className="font-market text-[9px] uppercase tracking-[0.1em] text-copy-label">
            One-time payment link · sent {formatDate(r.linkSentOn)}
          </div>
          <div className="mt-2 flex items-stretch gap-2">
            <div className="min-w-0 flex-1 break-all border border-line bg-white px-2.5 py-2 font-market text-[10.5px] text-gold-800">
              {joinUrl}
            </div>
            <button
              type="button"
              onClick={() => void copy()}
              className={`flex-none border px-3 font-market text-[10px] uppercase tracking-wide transition-all duration-200 ${
                copied
                  ? 'border-sea-300 bg-sea-50 text-sea-600'
                  : 'border-line bg-white text-copy-muted hover:border-ink-700 hover:text-ink-700'
              }`}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      <div className="mt-3.5 flex flex-wrap gap-2">
        {r.status === 'pending' && (
          <>
            <Button tone="p" onClick={onSendLink}>
              Approve &amp; create payment link
            </Button>
            <Button tone="g" onClick={onDecline}>
              Decline
            </Button>
          </>
        )}
        {r.status === 'link_sent' && (
          <>
            <Button tone="d" onClick={onResend}>
              Resend — new link
            </Button>
            {/* Not everyone pays on the link. Somebody hands over notes at the
                ground, and without this the request sits here for ever —
                Activate only ever opens for a request marked paid. */}
            {onCash && (
              <Button tone="d" onClick={onCash}>
                Record cash
              </Button>
            )}
            <Button tone="g" onClick={onDecline}>
              Decline
            </Button>
          </>
        )}
        {r.status === 'paid' && (
          <Button tone="p" onClick={onActivate}>
            Activate membership
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * A member the committee added directly — `status: 'pending'` until the
 * activation here. The card walks the same story as a request card: chase the
 * money, then ACTIVATE, which is what puts them on the roster. The password
 * is generated in the sheet that opens after activation, never before — the
 * pipeline is pay → activate → password.
 */
function AddedMemberCard({
  member: m,
  paid,
  payment,
  grounds,
  onPayLink,
  onCash,
  onActivate,
  highlighted = false,
  preferCash = false,
}) {
  const at = addedStageIndex(m, paid);
  const ref = useRef(null);
  // Bring the just-added person into view once, on arrival.
  useEffect(() => {
    if (highlighted) ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlighted]);
  const age = ageFrom(m.dob);
  // The CLUB's amount, not the platform default — this card was quoting
  // ₹1,500 beside a link that charged the club's ₹2,000.
  const fees = useFees();

  return (
    <div
      ref={ref}
      className={`mb-3 overflow-hidden border bg-white ${
        highlighted ? 'border-gold-500 ring-4 ring-gold-500/25' : 'border-line'
      }`}
    >
      <div className={`border-l-4 p-4 ${paid ? 'border-l-sea-600' : 'border-l-indigo-400'}`}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2 font-market text-[19px] font-bold tracking-[-0.025em]">
            {m.name}
            {highlighted && (
              <span className="bg-gold-500 px-2 py-[3px] font-market text-[9.5px] font-extrabold uppercase tracking-[0.16em] text-ink-800">
                Just added
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {paid ? (
              <Pill tone="ok">Paid · awaiting activation</Pill>
            ) : m.linkSent ? (
              <Pill tone="soon">Link sent · unpaid</Pill>
            ) : (
              <Pill tone="pend">Dues pending</Pill>
            )}
            <span className="font-market text-[10px] text-copy-label">bib {bibNo(m.bib)}</span>
          </div>
        </div>

        <Stepper stages={ADDED_STAGES} at={at} />

        <div className="mt-3 grid grid-cols-1 gap-px overflow-hidden bg-line sm:grid-cols-3">
          <Cell k="Mobile" v={m.phone} mono />
          <Cell k="Email" v={m.email} />
          <Cell k="Age" v={age ?? '—'} mono />
          <Cell k="Tier" v={`${TIERS[m.tier].label} ${formatMoney(memberFee(fees, m))}`} />
          <Cell k="Home ground" v={grounds[m.ground] ?? '—'} />
          <Cell k="Sign-in" v={m.hasPassword ? 'Password set' : 'After activation'} />
        </div>

        {/* Paid: the proof, same green strip as a paid request. */}
        {paid && payment && (
          <div className="mt-3 border border-sea-300 bg-sea-50 px-3.5 py-3">
            <div className="font-market text-[11px] text-sea-600">
              Paid {formatMoney(payment.amount)} on {formatDate(payment.date)} · receipt{' '}
              <b>{payment.receipt}</b>
            </div>
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          {paid ? (
            <Button tone="p" small onClick={() => onActivate(m)}>
              Activate membership
            </Button>
          ) : (
            preferCash ? (
              <>
                <Button tone="p" small onClick={() => onCash(m)}>
                  Record cash
                </Button>
                <Button tone="g" small onClick={() => onPayLink(m)}>
                  {m.linkSent ? 'Resend payment link' : 'Send payment link instead'}
                </Button>
              </>
            ) : (
              <>
                <Button tone="p" small onClick={() => onPayLink(m)}>
                  {m.linkSent ? 'Resend payment link' : 'Send payment link'}
                </Button>
                <Button tone="g" small onClick={() => onCash(m)}>
                  Record cash
                </Button>
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * One label/value cell in a fact grid.
 *
 * `span` matters more than it looks: these grids draw their dividers with
 * `gap-px` over a `bg-line` backing, so a row that does not fill its columns
 * does not leave whitespace — it leaves a solid beige block. Any grid whose
 * cell count is not a multiple of its column count must stretch its last cell.
 */
function Cell({ k, v, mono, span }) {
  return (
    <div className={`bg-white px-3.5 py-3 ${span ?? ''}`}>
      <div className="font-market text-[9.5px] font-bold uppercase tracking-[0.16em] text-copy-label">
        {k}
      </div>
      <div
        className={`mt-1 font-market text-[13px] font-semibold text-ink-700 ${
          mono ? 'tabular-nums' : ''
        }`}
      >
        {v}
      </div>
    </div>
  );
}
